<template>
  <!-- Tooltip for measurement tool -->
  <div
    v-if="tooltipContent !== ''"
    class="tooltip"
  >
    {{ tooltipContent }}
  </div>
  <div class="component-map">
    <div
      ref="mapRef"
      class="component-map__map"
    ></div>
    <MapScale :dpc="dpc" />
    <MapZoom />
    <MiniMap />
    <MapPrint
      ref="mapPrintRef"
    />
    <MapRight
      :layers-group="props___.layersGroup"
      :user-layers="userLayers"
      @change-zoom="onChangeLevelZoom"
    />
    <MapBottom />
  </div>
  <Toast
    position="top-center"
    class="toast__confirm-request"
  >
    <template #message="slotProps">
      <div class="flex flex-column">
        <div class="text-center">
          <span
            class="pi pi-exclamation-triangle"
            style="font-size: 3rem"
          />
          <h4>{{ slotProps.message.summary }}</h4>
          <p>{{ slotProps.message.detail }}</p>
        </div>
        <div class="grid p-fluid">
          <div
            v-if="slotProps.message.buttonConfirm"
            class="col-6"
          >
            <Button
              class="p-button-primary"
              :label="slotProps.message.buttonConfirm.label"
              @click="slotProps.message.buttonConfirm.onClick"
            />
          </div>
          <div
            v-if="slotProps.message.buttonReject"
            class="col-6"
          >
            <Button
              class="p-button-secondary"
              :label="slotProps.message.buttonReject.label"
              @click="slotProps.message.buttonReject.onClick"
            />
          </div>
        </div>
      </div>
    </template>
  </Toast>
  <Overlay ref="componentOverlayRef" />
</template>


<script setup>
// Vue
import {ref, onMounted, watch, toRaw, provide, reactive, computed} from 'vue';
import {useStore} from 'vuex';

// Openlayers
import {Map, View} from 'ol';
import {Group as LayerGroup} from 'ol/layer';
import {
  MouseWheelZoom,
  defaults as defaultInteractions,
} from 'ol/interaction';
import {toLonLat} from 'ol/proj';
import Collection from 'ol/Collection';
import {Vector as VectorSource} from 'ol/source';
import Feature from 'ol/Feature';
import {getCenter} from 'ol/extent';
import {GeoJSON} from 'ol/format';

// Sttedra
import {
  useImmer,
  //  useToast,
} from '@sttedra_ui/framework';

// Primevue
import Toast from 'primevue/toast';
// import {useToast as useToastPrimevue} from 'primevue/usetoast';
import Button from 'primevue/button';

// Config
import PROPS from './default-props';
import MapModel from './map.component-model';
import state from './map.component-state';
import {
  DEFAULT_MAP,
  GROUP_BASE_MAPS,
  GROUP_MINIMAP_MAPS,
  GROUP_OVER_MAPS,
  GROUP_LAYERS,
  GROUP_USER_LAYERS,
  GROUP_SEARCH,
  GROUP_DRAW,
  GROUP_SERVICES,
  GROUP_INTERSECTIONS,
} from './default-tile-layers';

// Components
import MapScale from './map-scale/MapScale';
import MiniMap from './mini-map/MiniMap';
import MapZoom from './map-zoom/MapZoom';
import MapPrint from './map-print/MapPrint';
import MapRight from './map-right/MapRight.vue';
import MapBottom from './MapBottom.vue';
import Overlay from './Overlay.vue';
import {findByCoords} from '@/components/searcher/cartociudad.api';

// Measurement
import {Draw} from 'ol/interaction';
import {getLength, getArea} from 'ol/sphere.js';
import {Vector as VectorLayer} from 'ol/layer';
import {Circle as CircleStyle, Fill, Stroke, Style} from 'ol/style.js';
import {LineString, Polygon} from 'ol/geom.js';

// Composables
import {generateFeatureMultiPolygon} from '@/composables/generate-feature';
import generateTileLayerFromCapabilities from '@/composables/generate-tile-layer-capabilities/generate-tile-layer-capabilities'; // eslint-disable-line max-len

// API
import {getAllInfoByPoint, getAllInfoByTown} from '@/api/calc/wps/wps.api';
import {EXTENT_VIEW_BBOX_3857} from '@/api/util-coordinates';
import {calculateZoomLevel} from '@/util-scale';

// #region Props, Emits
const props___ = defineProps(PROPS);
// #endregion

// Measurement
const content = '';
const tooltipContent = ref(content);
let measuringInteraction;

// #region Params, Ref, Reactives, Computed
// References
const mapRef = ref();
const instanceMapRef = ref();
const componentOverlayRef = ref();
const mapPrintRef = ref(null);

// State
const state$$ = reactive(state);
// Store
const store = useStore();
const userLayers = computed(() => store.state.userLayers.layers);
const mapCenter = computed(() => store.state.map.center);
const mapZoom = computed(() => store.state.map.zoom);
const triggerScale = computed(() => store.state.map.triggerScale);
const mapScaleMeters = computed(() => store.state.map.mapScaleMeters);
const triggerExtent = computed(() => store.state.map.triggerExtent);
const userExtent = computed(() => store.state.map.userExtent);
const triggerPrint = computed(() => store.state.map.triggerPrint);
const dpc = computed(() => store.state.map.screenDPC);
// Model
const modelMap = new MapModel(toRaw(props___));
const {ref: modelMapRef, update: updateMapRef} = useImmer(modelMap);

// uses
// const toast = useToast();
// const toastPrimeVue = useToastPrimevue();
// #endregion

// #region Functions

function updateState(values) {
  Object.keys(values).forEach((key) => {
    if (state$$[key] !== undefined) {
      if (
        Object.prototype.toString.call(state$$[key]) === '[object Object]' &&
        Object.prototype.toString.call(values[key]) === '[object Object]'
      ) {
        state$$[key] = {...state$$[key], ...values[key]};
      } else {
        state$$[key] = values[key];
      }
    }
  });
}
/**
 * Create the instance of map. Set the events and interactions
 *
 */
function createMap() {
  instanceMapRef.value = new Map({
    ...modelMapRef.value.options,
    interactions: defaultInteractions(modelMapRef.value.options.interactions),
    target: mapRef.value,
    layers: [
      GROUP_BASE_MAPS,
      GROUP_LAYERS,
      GROUP_USER_LAYERS,
      GROUP_SEARCH,
      GROUP_OVER_MAPS,
      GROUP_DRAW,
      GROUP_SERVICES,
      GROUP_INTERSECTIONS,
    ],
    view: new View({
      projection: modelMapRef.value.projection,
      center: modelMapRef.value.center,
      zoom: modelMapRef.value.zoom,
      minZoom: modelMapRef.value.minZoom,
      maxZoom: modelMapRef.value.maxZoom,
      constrainResolution: false,
      extent: EXTENT_VIEW_BBOX_3857,
    }),
  });

  // Change the interaction with the mouseWheelZoom
  instanceMapRef.value.addInteraction(
    new MouseWheelZoom({
      constrainResolution: true,
      duration: 0,
      timeout: 0,
      maxDelta: 10,
      useAnchor: true,
    }),
  );

  // Set events to map
  instanceMapRef.value.on('moveend', onMoveendMap);
  instanceMapRef.value.on('click', onClickMap);
  instanceMapRef.value.on('pointermove', onPointermoveMap);
}

/**
 * Method to change and manage measures
 *
 */
function onChangeMeasureMode(mode, notFirstDraw) {
  // delete previous draw (if exists)
  if (notFirstDraw) {
    instanceMapRef.value.getInteractions().pop();
  }
  // reset tooltip content
  tooltipContent.value = '';

  const measurementLayer = new VectorLayer({
    source: new VectorSource(),
  });

  // draw type 'Point' (none), if 0
  if (mode === 0) {
    measuringInteraction = new Draw({
      source: measurementLayer.getSource(),
      style: new Style(),
      type: 'Point',
    });
  } else {
    // draw type 'LineString' if 1 and 'Polygon' if 2
    tooltipContent.value = 'Haga clic en el mapa para comenzar a medir';
    measuringInteraction = new Draw({
      source: measurementLayer.getSource(),
      style: new Style({
        fill: new Fill({
          color: 'rgba(255, 255, 255, 0.2)',
        }),
        stroke: new Stroke({
          color: 'rgba(0, 0, 0, 0.5)',
          lineDash: [10, 10],
          width: 2,
        }),
        image: new CircleStyle({
          radius: 5,
          stroke: new Stroke({
            color: 'rgba(0, 0, 0, 0.7)',
          }),
          fill: new Fill({
            color: 'rgba(255, 255, 255, 0.2)',
          }),
        }),
      }),
      type: mode === 1 ? 'LineString' : 'Polygon',
    });
  }

  instanceMapRef.value.addInteraction(measuringInteraction);

  // set tooltip content everytime you move
  function updateMeasure(event) {
    const geometry = event.target.getGeometry();
    let output;
    if (geometry instanceof LineString) {
      const length = getLength(geometry);
      if (length > 1000) {
        output = Math.round((length / 1000) * 100) / 100 + ' ' + 'km';
      } else {
        output = Math.round(length * 100) / 100 + ' ' + 'm';
      }
    } else if (geometry instanceof Polygon) {
      const area = getArea(geometry);
      if (area > 10000) {
        output = Math.round((area / 1000000) * 100) / 100 + ' ' + 'km²';
      } else {
        output = Math.round(area * 100) / 100 + ' ' + 'm²';
      }
    }
    tooltipContent.value = output;
  }

  // event on draw start and move
  measuringInteraction.on('drawstart', (event) => {
    event.feature.on('change', (evt) => updateMeasure(evt));
  });
}

/**
 * Set the layers from model to group GROUP_BASE_MAPS
 *
 */
async function setLayersToGroupBaseMaps() {
  if (modelMapRef.value.maps) {
    const maps = modelMapRef.value.maps.map(async (map) => {
      const tileLayer = await generateTileLayerFromCapabilities(map);
      const tileLayerAlternative = await generateTileLayerFromCapabilities(
        map.alternativeRequest,
      );
      if (map.alternativeRequest && tileLayerAlternative) {
        return new LayerGroup({
          layers: [tileLayer, tileLayerAlternative],
          properties: map,
        });
      } else if (tileLayer) {
        return tileLayer;
      }
    });

    const tileLayers = await Promise.all(maps);
    GROUP_BASE_MAPS.setLayers(new Collection(tileLayers.filter((map) => map)));
  } else {
    GROUP_BASE_MAPS.setLayers(new Collection([DEFAULT_MAP]));
  }
}

/**
 * Set the layers from model to group GROUP_MINIMAP_MAPS
 *
 */
async function setLayersToGroupMinimapMaps() {
  if (modelMapRef.value.minimapMaps) {
    const minimapMaps = modelMapRef.value.minimapMaps.map(async (map) => {
      return await generateTileLayerFromCapabilities(map);
    });
    const tileLayers = await Promise.all(minimapMaps);
    GROUP_MINIMAP_MAPS.setLayers(
      new Collection(tileLayers.filter((map) => map)),
    );
  }
}

/**
 * Set the layers from model to group GROUP_OVER_MAPS
 *
 */
async function setLayersToGroupOverMaps() {
  if (modelMapRef.value.overMaps) {
    const overMaps = modelMapRef.value.overMaps.map(async (overMap) => {
      return await generateTileLayerFromCapabilities(overMap);
    });
    const tileLayers = await Promise.all(overMaps);
    GROUP_OVER_MAPS.setLayers(
      new Collection(tileLayers.filter((overMap) => overMap)),
    );
  }
}

/**
 * Set the layers from model to group GROUP_LAYERS
 *
 */
async function setLayersToGroupLayers() {
  localStorage.removeItem('capabilities');
  if (modelMapRef.value.layersGroup) {
    const layersGroup = modelMapRef.value.layersGroup
      .filter((data) => data.url != 'undefined')
      .map(async (layer, index) => {
        if (index == 0) {
          return await generateTileLayerFromCapabilities(layer);
        }
      });

    await Promise.all(layersGroup).then(async (val) => {
      const layersGroup2 = modelMapRef.value.layersGroup.map(
        async (layer, index) => {
          return await generateTileLayerFromCapabilities(layer);
        },
      );
      const tileLayers = await Promise.all(layersGroup2);
      GROUP_LAYERS.setLayers(
        new Collection(tileLayers.filter((layer) => layer)),
      );
    });
  }
}

/**
 * Set the layers from model to group GROUP_USER_LAYERS
 *
 */
async function setLayersToGroupUserLayers(newUserLayers) {
  const layerArray = newUserLayers.map((ul) => ul.olLayer);
  GROUP_USER_LAYERS.setLayers(new Collection(layerArray));
}

/**
 * Load the default layers with the PROPS values
 *
 */
async function loadLayers() {
  setLayersToGroupBaseMaps();
  setLayersToGroupMinimapMaps();
  setLayersToGroupOverMaps();
  setLayersToGroupLayers();
}

/**
 * Show overlay with the info of a town
 *
 * @param {Feature} featureSearch
 * @param {Event} event
 */
async function getAllInfoByClickTown(featureSearch, event) {
  const center = getCenter(featureSearch.getGeometry().getExtent());
  setValueOverlay(center, {
    type: 'loading',
  });
  if (featureSearch.getProperties().request) {
    setInfoOverlayFeature(featureSearch.getProperties().request, featureSearch);
    return;
  }

  let info = null;

  // const tokenCaptcha = STTEDRA_CUSTOM_CAPTCHA ?
  //   await useReCaptcha({
  //     key: STTEDRA_CUSTOM_CAPTCHA_TOKEN,
  //     moveRecaptcha: {
  //       function: moveReCaptcha,
  //     },
  //   }) :
  //   null;
  const tokenCaptcha = null; // TODO: Remove in future refactor

  if (featureSearch.getGeometry().getType() === 'Polygon') {
    const coordsLatLon = featureSearch
      .getGeometry()
      .getCoordinates()[0]
      .map((coord) => {
        const [lon, lat] = toLonLat(coord);
        return {lat, lon};
      });

    info = await getAllInfoByTown({polygons: [coordsLatLon], tokenCaptcha});
    featureSearch.setProperties({request: info});
  } else if (featureSearch.getGeometry().getType() === 'MultiPolygon') {
    const coordsLatLon = featureSearch
      .getGeometry()
      .getCoordinates()
      .map((groupCoords) => {
        return groupCoords[0].map((coord) => {
          const [lon, lat] = toLonLat(coord);
          return {lat, lon};
        });
      });

    info = await getAllInfoByTown({polygons: coordsLatLon, tokenCaptcha});
    featureSearch.setProperties({request: info});
  } else if (featureSearch.getGeometry().getType() === 'Point') {
    info = await getInfoPoint(featureSearch, tokenCaptcha);
  }

  if (info) {
    const {name} = featureSearch.getProperties();
    const title = name ? `Información de ${name}` : `Información de posición`;
    const [lon, lat] = center ? toLonLat(center) : toLonLat(event.coordinate);
    setValueOverlay(center, {
      type: 'town',
      Name: title,
      feature: featureSearch,
      coordinate: {lat, lon},
      properties: info,
    });
  } else {
    // close the overlay
    setValueOverlay();
  }
}

/**
 * Function to move the recaptcha
 *
 * @param {ObjectHTML} captchaComponent
 *
 */
// function moveReCaptcha(captchaComponent) {
//   if (captchaComponent) {
//     const newParent = document.getElementById('content__google__captcha');
//     captchaComponent.parentElement.removeChild(captchaComponent);
//     captchaComponent.style.bottom = 'auto';
//     newParent.style.height = '60px';
//     newParent.appendChild(captchaComponent);
//   }
// }

/**
 * Set the info to the Overlay
 * Set the feature to ZSA and Huellas de Ruido
 *
 * @param {Object} info
 * @param {Feature} feature
 *
 */
function setInfoOverlayFeature(info, feature) {
  if (info) {
    if (
      info.polygonIntersectionsHuellasRuido &&
      info.polygonIntersectionsHuellasRuido.length > 0
    ) {
      const polygon = generateFeatureMultiPolygon(
        info.polygonIntersectionsHuellasRuido,
        info.coordinateSystem,
      );
      const source = new VectorSource({
        features: [polygon],
      });
      const layer = GROUP_INTERSECTIONS.getLayers()
        .getArray()
        .find((l) => {
          return l.getProperties().name === 'LAYER_HUELLAS';
        });

      if (layer && source) {
        layer.setSource(source);
      }
    }
    if (
      info.polygonIntersectionsZSA &&
      info.polygonIntersectionsZSA.length > 0
    ) {
      const polygon = generateFeatureMultiPolygon(
        info.polygonIntersectionsZSA,
        info.coordinateSystem,
      );
      const source = new VectorSource({
        features: [polygon],
      });
      const layer = GROUP_INTERSECTIONS.getLayers()
        .getArray()
        .find((l) => {
          return l.getProperties().name === 'LAYER_ZSA';
        });

      if (layer && source) {
        layer.setSource(source);
      }
    }
    const center = getCenter(feature.getGeometry().getExtent());
    const [lon, lat] = toLonLat(center);
    setValueOverlay(center, {
      type: feature.getGeometry().getType(),
      feature,
      coordinate: {lat, lon},
      properties: info,
    });
  }
}

/**
 * Request to get the info of the point
 *
 * @param {Feature} feature
 *
 * @return {Object}
 */
async function getInfoPoint(feature, tokenCaptcha) {
  setValueOverlay(getCenter(feature.getGeometry().getExtent()), {
    type: 'loading',
  });

  const [lon, lat] = toLonLat(feature.getGeometry().getCoordinates());
  const geom = [new Feature(feature.getGeometry())];
  const writer = new GeoJSON();
  const featureGeo = writer.writeFeatures(geom);
  const affectedTown = (await getAffectedTowns([{lat, lon}]))[0] ?? null;

  const infoRequest = await getAllInfoByPoint({
    point: {lat, lon},
    geoJSON: JSON.stringify(featureGeo),
    tokenCaptcha,
    affectedTown,
  });
  // Set the request to feature
  feature.setProperties({request: infoRequest});
  if (!infoRequest) {
    // Close the overlay
    setValueOverlay();
  }
  return infoRequest;
}

/**
 * Request to get the info of the polygon
 *
 * @param {Feature} feature
 */
/*
async function tryGetInfoPolygon(feature, tokenCaptcha) {
  toastPrimeVue.removeAllGroups();
  const area = feature.getGeometry().getArea();
  if ((area / 1000000) < modelMapRef.value.maxSizePolygonRequest) {
    if ((area / 1000000) > modelMapRef.value.warningSizePolygonRequest) {
      toastPrimeVue.add({
        severity: 'warn',
        summary: 'El polígono es muy grande',
        detail: `El polígono es muy grande, ¿está seguro de querer hacer la consulta?
          Puede tardar más de lo habitual.`,
        buttonConfirm: {
          label: 'Sí',
          onClick: async () => {
            setValueOverlay(
              getCenter(feature.getGeometry().getExtent()),
              {
                type: 'loading',
              },
            );
            toastPrimeVue.removeAllGroups();
            const info = await getInfoPolygon(feature, tokenCaptcha);
            if (info) {
              setInfoOverlayFeature(info, feature);
            } else {
              setValueOverlay(undefined);
            }
          },
        },
        buttonReject: {
          label: 'No',
          onClick: toastPrimeVue.removeAllGroups,
        },
      });
    } else {
      setValueOverlay(
        getCenter(feature.getGeometry().getExtent()),
        {
          type: 'loading',
        },
      );

      const info = await getInfoPolygon(feature, tokenCaptcha);
      if (info) {
        setInfoOverlayFeature(info, feature);
      } else {
        setValueOverlay(undefined);
      }
    }
  } else {
    toast.add({
      type: 'danger',
      title: 'Polígono demasiado grande',
      message: 'El polígono es demasiado grande para hacer la consulta',
      life: 5000,
    });
    return null;
  }
}*/

/**
 * Return the info of request's polygon
 *
 * @param {Feature} feature
 * @param {String} tokenCaptcha
 */
/*
async function getInfoPolygon(feature, tokenCaptcha) {
  let area = feature.getGeometry().getArea();
  if (area > 1000000) {
    // toLocaleString para que aparezca separado en formato correcto
    area = Number((area/1000000).toFixed(2)).toLocaleString('de-DE') + ' km²';
  } else {
    // toLocaleString para que aparezca separado en formato correcto
    area = Number(area.toFixed(2)).toLocaleString('de-DE') + ' m²';
  }

  const polygons = feature.getGeometry().getCoordinates().map((coordPolygon) => {
    return coordPolygon.map((coord) => {
      const [lon, lat] = toLonLat(coord);
      return {lat, lon};
    });
  });

  // Se obtiene el centro
  const [lon, lat] = toLonLat(getCenter(feature.getGeometry().getExtent()));
  const geom = [new Feature(feature.getGeometry().clone().transform('EPSG:3857', 'EPSG:4326'))];
  const writer = new GeoJSON();
  const featureGeo = writer.writeFeatures(geom);
  const affectedTowns = await getAffectedTowns([...polygons[0], {lat, lon}]);

  const infoRequest = await getAllInfoByPolygon({
    polygons,
    tokenCaptcha,
    geoJSON: JSON.stringify(featureGeo),
    area,
    affectedTowns,
  });
  feature.setProperties({request: infoRequest});
  return infoRequest;
}*/

/**
 * Return the towns that has hit
 *
 * @param {Array} array Array of coords
 *
 * @return {Array}
 */
async function getAffectedTowns(array) {
  const affectedTowns = [];
  for (const {lat, lon} of array) {
    const info = await findByCoords(lat, lon);
    if (info) {
      // eslint-disable-next-line max-len
      const name = `${info.muni ? info.muni + ', ' : ''}${info.province} (${
        info.postalCode
      })`;
      if (
        !affectedTowns.some((text) => {
          return text === name;
        })
      ) {
        affectedTowns.push(name);
      }
    }
  }
  return affectedTowns;
}
// #endregion

// #region Events
/**
 * Fired from event pointermove from map
 *
 * @param {Event} event
 *
 */
function onPointermoveMap(event) {
  updateMapRef((draft) => {
    draft.update({positionMouse: event.coordinate});
    return draft;
  });
}

/**
 * Fired from event moveend of the map
 *
 */
async function onMoveendMap() {
  const newZoom = instanceMapRef.value.getView().getZoom();

  const newCenter = instanceMapRef.value.getView().getCenter();
  const center = newCenter;
  await store.dispatch('map/setCenter', {center: center}, {root: true});
  await store.dispatch('map/setZoom', {newZoom}, {root: true});
  onChangeCenter(center);
  onChangeLevelZoom(newZoom);
}

/**
 * Fired from click on map
 *
 * @param {Event} event
 *
 */
async function onClickMap(event) {
  // Close the overlay
  setValueOverlay();
  GROUP_INTERSECTIONS.getLayers()
    .getArray()
    .forEach((layer) => {
      layer.setSource(null);
    });

  const featureSearch = GROUP_SEARCH.getLayers()
    .getArray()[0]
    ?.getSource()
    .getFeaturesAtCoordinate(event.coordinate)[0];

  if (featureSearch) {
    await getAllInfoByClickTown(featureSearch, event);
  } else {
    const overMapLayer = GROUP_OVER_MAPS.getLayers()
      .getArray()
      .find((layer) => {
        return (
          layer.getVisible() && layer.getProperties().values.request.urlGetInfo
        );
      });

    if (overMapLayer) {
      const request = {
        resolution: event.map.getView().getResolution(),
        zoom: event.map.getView().getZoom(),
        source: overMapLayer.getSource(),
        coordinate: event.coordinate,
      };
      const response = await overMapLayer
        .getProperties()
        .values.getUrlInfo(request);
      if (response && response.features && response.features.length > 0) {
        const {properties} = response.features[0];
        if (
          properties.Discriminator === 'Radioelectric' ||
          properties.Discriminator === 'Airport'
        ) {
          const [lon, lat] = toLonLat(event.coordinate);
          setValueOverlay(event.coordinate, {
            type: 'airport',
            coordinate: {lat, lon},
            properties,
          });
        }
      }
    }
  }
}

/**
 * Event when a feature from draw has been clicked
 *
 * @param {Object} object
 *    {
 *      {Event} event,
 *      {Feature} feature
 *    }
 */
/*
async function onClickFeatureDraw({feature}) {
  if (feature.getProperties().request) {
    setInfoOverlayFeature(feature.getProperties().request, feature);
    return;
  }

  const tokenCaptcha = STTEDRA_CUSTOM_CAPTCHA ?
    await useReCaptcha({
      key: STTEDRA_CUSTOM_CAPTCHA_TOKEN,
      moveRecaptcha: {
        function: moveReCaptcha,
      },
    }) :
    null;

  const type = feature.getGeometry().getType();

  if (type === 'Point') {
    const info = await getInfoPoint(feature, tokenCaptcha);
    if (info) {
      setInfoOverlayFeature(info, feature);
    } else {
      // Close the overlay
      setValueOverlay();
    }
  } else if (type === 'Polygon') {
    await tryGetInfoPolygon(feature, tokenCaptcha);
  }
}*/

/**
 * Event when the center has been changed
 *
 * @param {Array} center
 */
function onChangeCenter(center) {
  updateMapRef((draft) => {
    draft.update({center});
    return draft;
  });
}

/**
 * Event when the center has been changed
 *
 * @param {Array} center
 */
function onChangeCenterProps(newCenter) {
  const center = newCenter;
  updateMapRef((draft) => {
    draft.update({center: newCenter});
    return draft;
  });
  instanceMapRef.value.getView().setCenter(center);
}

/**
 * Event when the zoom has been changed
 *
 * @param {Number} zoom
 */
async function onChangeLevelZoom(rawZoom) {
  const zoom = parseInt(rawZoom);
  const groupBaseMap = GROUP_BASE_MAPS.getLayers()
    .getArray()
    .find((layer) => {
      if (layer.getVisible()) {
        return layer;
      }
    });

  let minZoomGroup = modelMapRef.value.layersGroup[0]?.minZoom ?? 20;
  modelMapRef.value.layersGroup.forEach((layerGroup) => {
    if (layerGroup.minZoom > minZoomGroup) {
      minZoomGroup = layerGroup.minZoom;
    }
  });

  GROUP_LAYERS.setVisible(minZoomGroup < zoom);

  if (groupBaseMap && groupBaseMap instanceof LayerGroup) {
    groupBaseMap
      .getLayers()
      .getArray()
      .forEach((layer) => {
        if (layer.getMaxZoom() <= zoom) {
          layer.setVisible(false);
        } else if (layer.getMaxZoom() >= zoom) {
          layer.setVisible(true);
        }
      });
  }

  updateMapRef((draft) => {
    draft.update({zoom});
    return draft;
  });
  instanceMapRef.value.getView().setZoom(rawZoom);
}

/**
 * Set the value to state and show or hidden the overlay
 *
 * @param {Array} center
 * @param {Object} currentInfoOverlay
 */
function setValueOverlay(center = undefined, currentInfoOverlay = {}) {
  updateState({currentInfoOverlay});
  componentOverlayRef.value.setPosition(center);
}
// #endregion

// #region Effects, Hooks
onMounted(async () => {
  createMap();
  onChangeMeasureMode(0, false);
});
watch([mapCenter, mapZoom], ([newCenter, newZoom]) => {
  onChangeCenterProps(newCenter);
  onChangeLevelZoom(newZoom);
});
// Scale trigger
watch([triggerScale, mapScaleMeters], async ([newTrigger, newScaleMeters]) => {
  if (!!newTrigger && !!newScaleMeters && instanceMapRef?.value) {
    const map = instanceMapRef.value;
    const newZoom = calculateZoomLevel(map, newScaleMeters, dpc.value);
    if (!!newZoom) {
      onChangeLevelZoom(newZoom);
      // await store.dispatch('map/setZoom', {zoom: newZoom}, {root: true});
    }
  }
});
// Zoom trigger
watch([triggerExtent, userExtent], async ([newTrigger, newExtent]) => {
  if (!!newTrigger && !!newExtent?.length) {
    const map = instanceMapRef.value;
    map.getView().fit(newExtent, map.getSize());
  }
});
// Print trigger
watch(triggerPrint, async (newTrigger) => {
  if (!!newTrigger && !!mapPrintRef?.value) {
    mapPrintRef.value.printMap();
  }
});
watch(
  userLayers,
  (newUserLayers) => {
    setLayersToGroupUserLayers(newUserLayers);
  },
  {deep: true},
);
watch(props___, () => {
  updateMapRef((draft) => {
    draft.update(props___);
    onChangeCenterProps(props___.center);
    onChangeLevelZoom(props___.zoom);
    return draft;
  });
  setTimeout(async () => {
    await loadLayers();
  }, 1000);
});
// #endregion

// #region Provide, Expose
provide('modelMapRef', modelMapRef);
provide('GROUP_BASE_MAPS', GROUP_BASE_MAPS);
provide('GROUP_MINIMAP_MAPS', GROUP_MINIMAP_MAPS);
provide('GROUP_OVER_MAPS', GROUP_OVER_MAPS);
provide('GROUP_LAYERS', GROUP_LAYERS);
provide('GROUP_USER_LAYERS', GROUP_USER_LAYERS);
provide('GROUP_SEARCH', GROUP_SEARCH);
provide('GROUP_DRAW', GROUP_DRAW);
provide('GROUP_SERVICES', GROUP_SERVICES);
provide('instanceMapRef', instanceMapRef);
provide('onChangeCenterProps', onChangeCenterProps);
provide('onChangeLevelZoom', onChangeLevelZoom);
provide('state$$', state$$);
// #endregion
</script>

<style lang="scss" scoped>
.component-map {
  width: 100%;
  height: 100%;

  &__map {
    width: 100%;
    height: 100%;
  }
}
.hidden {
  cursor: none;
}
.toast__confirm-request {
  z-index: 999;

  & > div {
    display: flex;
    justify-content: center;
  }
}

/* Measurement tooltip style*/
.tooltip {
  margin-top: 1.5%;
  margin-left: 45%;
  margin-right: 45%;
  display: block;
  text-align: center;
  position: absolute;
  cursor: default;
  opacity: 1;
  font-weight: bold;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 7px;
  border-radius: 10px;
  z-index: 1000;
}
</style>
